import { getTrackCommerceFlags } from "./trackCommerceCompatibility";

export const DEFAULT_BEAT_PRICING = Object.freeze({
  leaseBeatPrice: 30,
  exclusiveBeatPrice: 300,
});

function validPrice(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function roundCurrency(value) {
  return Math.round(Math.max(0, Number(value) || 0) * 100) / 100;
}

function roundBeatPrice(value) {
  return Math.max(0, Math.round(Number(value) || 0));
}

export function normalizeBeatPackPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  const percent = number > 0 && number <= 1 ? number * 100 : number;
  return Math.max(0, Math.min(100, percent));
}

export function applyBeatPackDiscount(price, percent, eligibleCount, minimumCount = 10) {
  const basePrice = roundCurrency(price);
  const normalizedPercent = normalizeBeatPackPercent(percent);
  const qualifies = Number(eligibleCount) >= minimumCount && normalizedPercent > 0;
  return {
    basePrice,
    price: qualifies
      ? roundCurrency(basePrice * (1 - normalizedPercent / 100))
      : basePrice,
    discounted: qualifies,
    qualifies,
    percent: qualifies ? normalizedPercent : 0,
  };
}

/**
 * Resolve display prices with one explicit precedence chain. The checkout
 * functions still re-read tracks/settings and calculate the authoritative
 * amount at payment time.
 */
export function resolveBeatPrices(track = {}, globals = {}, globalPriceMultiplier = 1) {
  const configuredLease = validPrice(globals.leaseBeatPrice) ?? DEFAULT_BEAT_PRICING.leaseBeatPrice;
  const configuredExclusive = validPrice(globals.exclusiveBeatPrice) ?? DEFAULT_BEAT_PRICING.exclusiveBeatPrice;
  const trackLease = validPrice(track.leasePrice);
  const legacyLease = validPrice(track.price);
  const trackExclusive = validPrice(track.exclusivePrice);
  const flags = getTrackCommerceFlags(track);

  // Lease: structured per-track price, legacy per-track price, configured default.
  let lease = trackLease ?? legacyLease ?? configuredLease;
  // Exclusive: structured per-track price, configured default, then the legacy
  // ten-times-lease fallback only when no configured default is usable.
  let exclusive = trackExclusive ?? configuredExclusive ?? (lease * 10);

  const structuredSale = validPrice(track.salePercent);
  const salePercent = structuredSale ?? (flags.halfOff ? 50 : 0);
  const specialPrice = validPrice(track.specialPrice);

  // Compatibility rules remain centralized here while title markers are phased out.
  if (flags.special && specialPrice != null) {
    lease = specialPrice;
    if (trackExclusive == null) exclusive = specialPrice * 10;
  }
  if (flags.original) {
    if (trackLease == null) lease = 1;
    if (trackExclusive == null) exclusive = 10;
  }
  if (salePercent > 0) {
    const multiplier = Math.max(0, 1 - Math.min(100, salePercent) / 100);
    lease *= multiplier;
    exclusive *= multiplier;
  }
  if (flags.freeLease) lease = 0;

  const multiplier = Number.isFinite(Number(globalPriceMultiplier))
    ? Math.max(0, Number(globalPriceMultiplier))
    : 1;

  return {
    lease: roundBeatPrice(lease * multiplier),
    exclusive: roundBeatPrice(exclusive * multiplier),
    hidePrice: flags.sold || flags.remix || track.availableForSale === false,
    hasFire: flags.original,
    flags,
  };
}
